/**
 * Invite people to the active organization (docs/Decisions.md D51 item 2).
 * Reached from the nav by anyone the API says may invite (`canInvite` on
 * GET /organizations/mine): org admins, moderators and members of the groups
 * an org admin allowed. Invitations from here are always as a member; an org
 * admin chooses other roles in Org settings. The invite route decides again,
 * so a stale nav item can never invite anyone.
 */
import { useState } from 'react';
import { useOrg } from '../lib/org';
import * as orgApi from '../api/organizations';
import { useToast } from '../components/toast';
import { ErrorAlert, Field } from '../components/ui';
import { cn, ui } from '../lib/ui';

export default function InvitePeople() {
  const { activeOrg } = useOrg();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!activeOrg) {
    return <p className={ui.muted}>Choose an organization first.</p>;
  }
  if (!activeOrg.canInvite) {
    return (
      <div className={ui.alertInfo} role="status">
        Only organization admins, moderators and members of the groups allowed to invite can invite
        people to {activeOrg.name}.
      </div>
    );
  }

  async function onSubmit(event) {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    setBusy(true);
    setError(null);
    try {
      await orgApi.inviteMember(activeOrg.orgId, address, 'member');
      toast.success(`Invitation sent to ${address}`);
      setEmail('');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1 className={ui.h1}>Invite people to {activeOrg.name}</h1>
      <form onSubmit={onSubmit} className={cn(ui.card, 'max-w-xl')}>
        <ErrorAlert error={error} />
        <p className={cn(ui.muted, 'text-body-s')}>
          They get an email with a link to join as a member. When they accept, it counts toward your
          friend ribbons.
        </p>
        <Field
          label="Email address"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
        <button type="submit" className={ui.btnPrimary} disabled={busy || !email.trim()}>
          {busy ? 'Sending…' : 'Send invitation'}
        </button>
      </form>
    </>
  );
}
